/**
 * Composable per Managers autocomplete con loading e error handling centralizzati
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import type { BaseAutocompleteOptions } from '@/lib/autocompletes';
import { getManagersAutocomplete } from '@/lib/autocompletes/managers';
import { WorkerAutocompleteResult } from '~/lib/models/worker';

export function useManagersAutocomplete(options: BaseAutocompleteComposableOptions = {}) {
	const managers = ref<WorkerAutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchManagers(options: BaseAutocompleteOptions = {}): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getManagersAutocomplete(buildOdataQuery, options);
				if (token.isLatest()) {
					managers.value = result;
				}
			} catch (err) {
				console.error('❌ ERROR useManagersAutocomplete.searchManagers:', err);
				error('Errore durante il caricamento dei manager! Ricaricare la pagina e riprovare.');
				if (token.isLatest()) {
					managers.value = [];
				}
			}
		}, 'searchManagers');
	}

	function clearManagers(): void {
		managers.value = [];
	}

	return {
		managers: readonly(managers),
		searchManagers,
		clearManagers,
	};
}
